import { slabImageUrl } from './imageUrls.mjs';
import { residentIndices } from './showcaseWindow.mjs';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  animate, motion, useMotionTemplate, useMotionValue, useMotionValueEvent, useSpring, useTransform,
} from 'framer-motion';
import { IconChevronLeft, IconChevronRight, IconDeviceMobileRotated, IconRotate } from '@tabler/icons-react';

const SLAB_RATIO = 0.6; // width / height of a graded slab
const clamp = (value, low, high) => Math.min(high, Math.max(low, value));
const scanRatios = new Map();
const failedScans = new Set();

function measure(width, height) {
  const compact = width < 700;
  const availableHeight = compact ? Math.max(0, height - 54) : height * 0.8;
  const slabW = Math.max(60, Math.min(compact ? width * 0.56 : Math.min(width * 0.3, 360), availableHeight * SLAB_RATIO));
  const slabH = slabW / SLAB_RATIO;
  return {
    compact,
    slabW,
    slabH,
    top: Math.max(10, (height - slabH) * 0.3),
    gap: slabW * (compact ? 0.76 : 0.86),
    gapFar: slabW * (compact ? 0.24 : 0.34),
    depth: slabW * 0.72,
    depthFar: slabW * 0.3,
    perspective: Math.round(slabW * 5.2),
    reach: compact ? 3 : 5,
  };
}

// Where a slab sits in the room for a given distance (in slabs) from the focus point.
function place(offset, metrics) {
  const distance = Math.abs(offset);
  const side = offset < 0 ? -1 : 1;
  const near = Math.min(distance, 1);
  const far = Math.max(distance - 1, 0);
  return {
    x: side * (near * metrics.gap + far * metrics.gapFar),
    z: -(near * metrics.depth + far * metrics.depthFar),
    rotate: -side * near * 50,
    shade: Math.min(0.8, near * 0.32 + far * 0.13),
    fade: distance > metrics.reach ? clamp(1 - (distance - metrics.reach) * 1.6, 0, 1) : 1,
  };
}

function SlabStandIn({ slab, side, loading }) {
  return (
    <div className={`sv-standin${loading ? ' is-loading' : ''}`}>
      <div className="sv-standin-label">
        <span>{slab.gradeKey || 'Graded slab'}</span>
        <strong>{side === 'back' ? 'Back of slab' : slab.name}</strong>
      </div>
      <div className="sv-standin-window">{!loading ? <span>{side === 'back' ? 'No back scan' : 'Scan unavailable'}</span> : null}</div>
    </div>
  );
}

export const SlabScan = memo(function SlabScan({ src, previewSrc, side, slab, eager, children }) {
  const identity = `${slab.key || slab.cert || slab.id || slab.name}:${side}`;
  const initialSrc = previewSrc && !failedScans.has(previewSrc) ? previewSrc : src;
  const [scan, setScan] = useState(null);
  const [failure, setFailure] = useState(null);
  const image = useRef(null);
  const request = useRef(null);
  request.current = { identity, src: initialSrc };
  const visible = scan?.identity === identity ? scan : null;
  const acceptInitial = useCallback((element) => {
    // Native load/complete is enough for the image already in the DOM. Waiting
    // another decode frame here makes cached scans blink on fast re-entry.
    if (!element.naturalWidth) return;
    if (request.current.identity !== identity || request.current.src !== initialSrc) return;
    const next = element.naturalWidth / element.naturalHeight;
    const ratio = next > 0.3 && next < 1.4 ? next : SLAB_RATIO;
    scanRatios.set(initialSrc, ratio);
    setScan(current => current?.identity === identity && current?.src === initialSrc ? current : { identity, src: initialSrc, ratio });
  }, [identity, initialSrc]);
  useLayoutEffect(() => {
    if (!visible && image.current?.complete && image.current.naturalWidth > 0) acceptInitial(image.current);
  }, [visible, acceptInitial]);
  useEffect(() => {
    // Initial images retain native lazy loading. Only replacements preload here.
    if (!src || failedScans.has(src) || scan?.identity !== identity || scan.src === src) return undefined;
    let cancelled = false;
    let settling = false;
    const image = new Image();
    const failed = () => {
      if (cancelled) return;
      failedScans.add(src);
      setFailure(src);
    };
    const loaded = async () => {
      if (settling) return;
      settling = true;
      try { await image.decode?.(); } catch { if (!image.naturalWidth) { failed(); return; } }
      if (cancelled) return;
      const next = image.naturalWidth / image.naturalHeight;
      const ratio = next > 0.3 && next < 1.4 ? next : SLAB_RATIO;
      scanRatios.set(src, ratio);
      setScan({ identity, src, ratio });
    };
    image.decoding = 'async';
    image.onload = loaded;
    image.onerror = failed;
    image.src = src;
    if (image.complete && image.naturalWidth > 0) loaded();
    return () => { cancelled = true; image.onload = null; image.onerror = null; image.removeAttribute('src'); };
  }, [src, identity, scan]);
  // Keep this slab's decoded scan visible until its replacement is decoded too.
  // A different slab or side must never inherit the previous image.
  const state = visible ? 'ready' : !src || failedScans.has(src) || failure === src ? 'error' : 'loading';
  const ratio = visible?.ratio || scanRatios.get(src) || SLAB_RATIO;
  // Fit the box to the scan so light and shade land on the slab, not around it.
  const box = ratio >= SLAB_RATIO
    ? { width: '100%', height: `${(SLAB_RATIO / ratio) * 100}%` }
    : { width: `${(ratio / SLAB_RATIO) * 100}%`, height: '100%' };
  return (
    <div className="sv-scan">
      <div className="sv-scan-box" style={state === 'ready' ? box : undefined}>
        {state !== 'ready' ? <SlabStandIn slab={slab} side={side} loading={state === 'loading'} /> : null}
        {visible || (src && state !== 'error') ? (
          <img
            ref={image}
            src={visible?.src || initialSrc}
            alt=""
            draggable="false"
            decoding="async"
            loading={eager ? 'eager' : 'lazy'}
            className={visible ? 'is-ready' : ''}
            onLoad={visible ? undefined : (event) => acceptInitial(event.currentTarget)}
            onError={visible ? undefined : () => { failedScans.add(initialSrc); setFailure(initialSrc); }}
          />
        ) : null}
        {children}
      </div>
    </div>
  );
});

function Light({ light }) {
  return (
    <>
      <motion.div className="sv-glare" style={{ background: light.glare, opacity: light.glareOpacity }} />
      <motion.div className="sv-holo" style={{ backgroundPosition: light.holoPosition, opacity: light.holoOpacity }} />
    </>
  );
}

const ShowcaseItem = memo(function ShowcaseItem({ slab, index, pos, metrics, isCenter, active, flipped, onActivate, eager, light }) {
  const transform = useTransform(pos, (value) => {
    const { x, z, rotate } = place(index - value, metrics);
    return `perspective(${metrics.perspective}px) translate3d(${x.toFixed(1)}px, 0px, ${z.toFixed(1)}px) rotateY(${rotate.toFixed(2)}deg)`;
  });
  const zIndex = useTransform(pos, (value) => 500 - Math.round(Math.abs(index - value) * 20));
  const shade = useTransform(pos, (value) => place(index - value, metrics).shade);
  const fade = useTransform(pos, (value) => place(index - value, metrics).fade);
  const turn = useSpring(flipped ? 180 : 0, { stiffness: 170, damping: 21 });
  useEffect(() => { turn.set(flipped ? 180 : 0); }, [turn, flipped]);
  const frontOpacity = useTransform(turn, (value) => (value < 90 ? 1 : 0));
  const backOpacity = useTransform(turn, (value) => (value >= 90 ? 1 : 0));
  const showBack = isCenter || flipped;
  const front = slabImageUrl(slab, 'front', isCenter ? 720 : 320);
  const back = slabImageUrl(slab, 'back', isCenter ? 720 : 320);
  const reflected = slabImageUrl(slab, flipped && back && !failedScans.has(back) ? 'back' : 'front', 320);
  const label = `${slab.name}${slab.gradeKey ? `, ${slab.gradeKey}` : ''}`;
  return (
    <motion.div
      className={`sv-item${isCenter ? ' is-center' : ''}`}
      style={{ transform, zIndex, opacity: fade, display: active ? undefined : 'none', willChange: active ? 'transform' : 'auto', width: metrics.slabW, height: metrics.slabH, marginLeft: -metrics.slabW / 2, top: metrics.top }}
      onClick={() => onActivate(index)}
      role="button"
      tabIndex={-1}
      aria-label={isCenter ? `${label}. Showing ${flipped ? 'back' : 'front'}. Select to flip.` : `Show ${label}`}
    >
      <motion.div className="sv-tilt" style={isCenter && light ? { rotateX: light.tiltX, rotateY: light.tiltY, transformPerspective: metrics.perspective * 0.8 } : undefined}>
        <motion.div className="sv-turn" style={{ rotateY: turn }}>
          <motion.div className="sv-side sv-side-front" style={{ opacity: frontOpacity }}>
            <SlabScan src={front} previewSrc={slabImageUrl(slab, 'front', 320)} side="front" slab={slab} eager={eager}>
              {isCenter && light ? <Light light={light} /> : null}
            </SlabScan>
          </motion.div>
          <motion.div className="sv-side sv-side-back" style={{ opacity: backOpacity }}>
            {showBack ? (
              <SlabScan src={back} previewSrc={slabImageUrl(slab, 'back', 320)} side="back" slab={slab} eager>
                {isCenter && light ? <Light light={light} /> : null}
              </SlabScan>
            ) : null}
          </motion.div>
        </motion.div>
      </motion.div>
      <motion.div className="sv-shade" style={{ opacity: shade }} />
      <div className="sv-reflection" aria-hidden="true">
        {reflected && !failedScans.has(reflected) ? <img src={reflected} alt="" draggable="false" decoding="async" loading={eager ? 'eager' : 'lazy'} /> : null}
      </div>
    </motion.div>
  );
});

function canTiltWithPhone() {
  if (typeof window === 'undefined' || !('DeviceOrientationEvent' in window)) return false;
  try { return window.matchMedia('(pointer: coarse)').matches; } catch { return false; }
}

export default function Showcase3D({ slabs, index, jump, onIndexChange, flipped, onFlip, resetKey, reduceMotion, allowPhoneTilt = true }) {
  const stage = useRef(null);
  const [size, setSize] = useState({ width: 390, height: 380 });
  useLayoutEffect(() => {
    const element = stage.current;
    if (!element) return undefined;
    const update = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const metrics = useMemo(() => measure(size.width, size.height), [size.width, size.height]);
  const total = slabs.length;

  const pos = useMotionValue(index);
  const [center, setCenter] = useState(index);
  const centerRef = useRef(index);
  const motionRun = useRef(null);
  const pendingJump = useRef(null);
  const resident = useRef({ resetKey, indices: [] });
  const gesture = useRef(null);
  const suppressClick = useRef(false);
  const introPlayed = useRef(false);
  const latest = useRef({ metrics, total, onIndexChange, onFlip });
  latest.current = { metrics, total, onIndexChange, onFlip };

  useMotionValueEvent(pos, 'change', (value) => {
    const next = clamp(Math.round(value), 0, Math.max(0, latest.current.total - 1));
    if (next === centerRef.current) return;
    centerRef.current = next;
    setCenter(next);
    latest.current.onIndexChange(next);
  });

  const goTo = useCallback((target, velocity = 0) => {
    const destination = clamp(Math.round(target), 0, Math.max(0, latest.current.total - 1));
    pendingJump.current = null;
    motionRun.current?.stop();
    const from = pos.get();
    // Mount the destination before moving the room; don't animate through cold cards.
    if (Math.abs(destination - from) > 6 || (reduceMotion && destination !== centerRef.current)) {
      pendingJump.current = destination;
      centerRef.current = destination;
      setCenter(destination);
      latest.current.onIndexChange(destination);
      return;
    }
    if (reduceMotion) { pos.set(destination); return; }
    motionRun.current = animate(pos, destination, { type: 'spring', stiffness: 210, damping: 30, mass: 0.9, velocity, restDelta: 0.0005 });
  }, [pos, reduceMotion]);

  useLayoutEffect(() => {
    if (pendingJump.current == null) return;
    const destination = pendingJump.current;
    pendingJump.current = null;
    pos.set(destination);
  }, [center, pos]);

  // A new result set: land on the requested slab. The very first load glides in once.
  useEffect(() => {
    motionRun.current?.stop();
    pendingJump.current = null;
    centerRef.current = index;
    setCenter(index);
    const start = Math.min(index + 3, Math.max(0, total - 1));
    if (introPlayed.current || reduceMotion || start === index) {
      pos.set(index);
    } else {
      pos.set(start);
      motionRun.current = animate(pos, index, { type: 'spring', stiffness: 90, damping: 20, restDelta: 0.0005 });
    }
    introPlayed.current = true;
  }, [resetKey]);

  // Arrow keys, the scrubber and the wall send explicit jumps; our own position reports never come back as commands.
  const lastJump = useRef(jump?.seq ?? 0);
  useEffect(() => {
    if (!jump || jump.seq === lastJump.current) return;
    lastJump.current = jump.seq;
    goTo(jump.to);
  }, [jump, goTo]);

  useEffect(() => () => motionRun.current?.stop(), []);

  // Light: tilt follows the mouse on desktop, or the phone itself once enabled.
  const tiltX = useSpring(0, { stiffness: 120, damping: 15 });
  const tiltY = useSpring(0, { stiffness: 120, damping: 15 });
  const glareX = useTransform(tiltY, [-14, 14], [4, 76]);
  const glareY = useTransform(tiltX, [-10, 10], [72, -12]);
  const glare = useMotionTemplate`radial-gradient(circle at ${glareX}% ${glareY}%, rgba(255,255,255,0.46), rgba(255,255,255,0.06) 38%, rgba(255,255,255,0) 60%)`;
  const glareOpacity = useTransform([tiltX, tiltY], ([x, y]) => 0.28 + Math.min(1, Math.hypot(x, y) / 11) * 0.5);
  const holoPosition = useMotionTemplate`${glareX}% ${glareY}%`;
  const holoOpacity = useTransform([tiltX, tiltY], ([x, y]) => 0.12 + Math.min(1, Math.hypot(x, y) / 11) * 0.42);
  const light = useMemo(() => ({ tiltX, tiltY, glare, glareOpacity, holoPosition, holoOpacity }), [tiltX, tiltY, glare, glareOpacity, holoPosition, holoOpacity]);

  const [phoneTilt, setPhoneTilt] = useState(false);
  const [tiltAvailable] = useState(() => allowPhoneTilt && canTiltWithPhone());
  useEffect(() => {
    if (!phoneTilt) return undefined;
    let baseline = null;
    const onOrientation = (event) => {
      if (event.beta == null || event.gamma == null) return;
      if (!baseline) baseline = { beta: event.beta, gamma: event.gamma };
      tiltY.set(clamp((event.gamma - baseline.gamma) / 18, -1, 1) * 14);
      tiltX.set(clamp(-(event.beta - baseline.beta) / 18, -1, 1) * 10);
    };
    window.addEventListener('deviceorientation', onOrientation);
    return () => window.removeEventListener('deviceorientation', onOrientation);
  }, [phoneTilt, tiltX, tiltY]);

  async function togglePhoneTilt() {
    if (phoneTilt) { setPhoneTilt(false); tiltX.set(0); tiltY.set(0); return; }
    try {
      const Orientation = window.DeviceOrientationEvent;
      if (typeof Orientation?.requestPermission === 'function' && (await Orientation.requestPermission()) !== 'granted') return;
      setPhoneTilt(true);
    } catch {
      // Permission refused: the slab simply stays still.
    }
  }

  const activate = useCallback((target) => {
    if (suppressClick.current) { suppressClick.current = false; return; }
    if (target === centerRef.current) latest.current.onFlip();
    else goTo(target);
  }, [goTo]);

  function onPointerDown(event) {
    if (event.target.closest('button')) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    motionRun.current?.stop();
    pendingJump.current = null;
    suppressClick.current = false;
    gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, start: pos.get(), moved: false, samples: [{ x: event.clientX, t: event.timeStamp }] };
  }

  function onPointerMove(event) {
    const drag = gesture.current;
    if (drag && drag.id === event.pointerId) {
      const dx = event.clientX - drag.x;
      const dy = event.clientY - drag.y;
      if (!drag.moved) {
        if (Math.abs(dx) < 6) return;
        if (Math.abs(dy) > Math.abs(dx)) { gesture.current = null; return; }
        drag.moved = true;
        event.currentTarget.setPointerCapture?.(event.pointerId);
      }
      const last = latest.current.total - 1;
      let next = drag.start - dx / latest.current.metrics.gap;
      if (next < 0) next *= 0.35;
      else if (next > last) next = last + (next - last) * 0.35;
      pos.set(next);
      drag.samples.push({ x: event.clientX, t: event.timeStamp });
      if (drag.samples.length > 6) drag.samples.shift();
      return;
    }
    if (event.pointerType === 'mouse' && !phoneTilt && stage.current) {
      const rect = stage.current.getBoundingClientRect();
      tiltY.set(clamp(((event.clientX - rect.left) / rect.width - 0.5) * 2, -1, 1) * 12);
      tiltX.set(clamp(-((event.clientY - rect.top) / rect.height - 0.5) * 2, -1, 1) * 9);
    }
  }

  function endGesture(event) {
    const drag = gesture.current;
    if (!drag || drag.id !== event.pointerId) return;
    gesture.current = null;
    if (!drag.moved) return;
    suppressClick.current = true;
    window.setTimeout(() => { suppressClick.current = false; }, 0);
    const first = drag.samples[0];
    const last = drag.samples[drag.samples.length - 1];
    const elapsed = Math.max(1, last.t - first.t);
    const slabsPerSecond = (-(last.x - first.x) / elapsed) * 1000 / latest.current.metrics.gap;
    goTo(pos.get() + clamp(slabsPerSecond, -40, 40) * 0.2, slabsPerSecond);
  }

  function onPointerLeave(event) {
    if (event.pointerType === 'mouse' && !phoneTilt) { tiltX.set(0); tiltY.set(0); }
  }

  useEffect(() => {
    const element = stage.current;
    if (!element) return undefined;
    let settle = 0;
    const onWheel = (event) => {
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!delta) return;
      event.preventDefault();
      motionRun.current?.stop();
      pendingJump.current = null;
      const step = event.deltaMode === 1 ? delta / 3 : delta / 110;
      pos.set(clamp(pos.get() + step, -0.3, latest.current.total - 0.7));
      window.clearTimeout(settle);
      settle = window.setTimeout(() => goTo(pos.get()), 120);
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    return () => { element.removeEventListener('wheel', onWheel); window.clearTimeout(settle); };
  }, [goTo, pos]);

  // Warm only nearby thumbnails immediately; fast scrolling must not keep
  // cancelling an idle timer before the next visible cards get a chance to load.
  const warmed = useRef(new Set());
  useEffect(() => {
    for (let i = center - metrics.reach - 3; i <= center + metrics.reach + 3; i += 1) {
      const src = slabs[i] && slabImageUrl(slabs[i], 'front', 320);
      if (!src || failedScans.has(src) || warmed.current.has(src)) continue;
      warmed.current.add(src);
      if (warmed.current.size > 128) warmed.current.delete(warmed.current.values().next().value);
      const image = new Image();
      image.decoding = 'async';
      image.src = src;
    }
  }, [center, slabs, metrics.reach]);

  const mounted = useMemo(() => {
    const previous = resident.current.resetKey === resetKey ? resident.current.indices : [];
    const indices = residentIndices(previous, center, metrics.reach, total, metrics.compact ? 12 : 32);
    resident.current = { resetKey, indices };
    return [...indices].sort((a, b) => a - b);
  }, [center, metrics.reach, metrics.compact, total, resetKey]);
  const items = [];
  for (const i of mounted) {
    items.push(
      <ShowcaseItem
        key={slabs[i].key}
        slab={slabs[i]}
        index={i}
        pos={pos}
        metrics={metrics}
        isCenter={i === center}
        active={Math.abs(i - center) <= metrics.reach + 1}
        flipped={i === center && flipped}
        onActivate={activate}
        eager={Math.abs(i - center) <= 2}
        light={i === center ? light : null}
      />,
    );
  }

  return (
    <div
      className="sv-stage"
      ref={stage}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endGesture}
      onPointerCancel={endGesture}
      onPointerLeave={onPointerLeave}
      style={{ '--sv-floor': `${Math.round(metrics.top + metrics.slabH)}px`, '--sv-slab-w': `${Math.round(metrics.slabW)}px` }}
      aria-roledescription="carousel"
      aria-label="Slab showcase. Swipe or use the arrow keys to browse."
    >
      <div className="sv-room" aria-hidden="true">
        <div className="sv-spot" />
        <div className="sv-floor" />
        <div className="sv-led" />
        <div className="sv-pool" />
      </div>
      <div className="sv-track">{items}</div>
      {total > 1 ? (
        <>
          <button type="button" className="sv-arrow sv-arrow-prev" onClick={() => goTo(centerRef.current - 1)} disabled={center <= 0} aria-label="Previous slab"><IconChevronLeft size={22} stroke={1.6} /></button>
          <button type="button" className="sv-arrow sv-arrow-next" onClick={() => goTo(centerRef.current + 1)} disabled={center >= total - 1} aria-label="Next slab"><IconChevronRight size={22} stroke={1.6} /></button>
        </>
      ) : null}
      <div className="sv-stage-tools">
        <button type="button" className="sv-stage-tool" onClick={() => latest.current.onFlip()} aria-pressed={flipped}>
          <IconRotate size={16} stroke={1.7} />
          <span>{flipped ? 'Front' : 'Back'}</span>
        </button>
        {tiltAvailable ? (
          <button type="button" className={`sv-stage-tool${phoneTilt ? ' is-on' : ''}`} onClick={togglePhoneTilt} aria-pressed={phoneTilt}>
            <IconDeviceMobileRotated size={16} stroke={1.6} />
            <span>{phoneTilt ? 'Tilt on' : 'Tilt to light'}</span>
          </button>
        ) : null}
      </div>
    </div>
  );
}
